import { useState } from 'react';
import { Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDateAU, formatRelativeDays } from '../../../shared/format';
import { QAEmpty } from '../components/QAEmpty';
import { QASkeleton } from '../components/QASkeleton';
import { KIND_META, TIMELINE_FILTER_OPTIONS } from '../components/timelineMeta';
import type { TimelineEntry, TimelineKind } from '../../../shared/api';

const LONG_DETAIL = 240;

/**
 * Practice-wide activity feed — the org-wide counterpart of the per-client
 * Timeline tab. Merges file notes, advice milestones, compliance events and
 * suite intake across every (non-archived) client, each linking to its record.
 */
export function Activity() {
  const [kind, setKind] = useState<'' | TimelineKind>('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading, isError } = useQuery({
    queryKey: ['activity', kind],
    queryFn: () =>
      api<{ data: TimelineEntry[] }>(`/api/dashboard/activity${kind ? `?kind=${kind}` : ''}`),
  });

  const entries = data?.data ?? [];

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <div>
      <div className="mb-6">
        <p className="qa-eyebrow mb-2">Activity</p>
        <h1 className="qa-h1">Across the <span className="qa-italic-accent">book.</span></h1>
        <p className="qa-deck mt-2 text-ink-soft">
          Recent file notes, advice, compliance updates and suite intake from every client.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 mb-5 flex-col sm:flex-row">
        <p className="text-sm text-ink-mute">
          {isLoading ? 'Loading activity…' : `${entries.length} recent event${entries.length === 1 ? '' : 's'}`}
        </p>
        <select
          className="qa-select sm:w-56"
          aria-label="Filter activity by type"
          value={kind}
          onChange={(e) => setKind(e.target.value as '' | TimelineKind)}
        >
          {TIMELINE_FILTER_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <QASkeleton variant="cards" rows={6} />
      ) : isError ? (
        <QAEmpty inline eyebrow="Couldn't load" title="The activity feed failed to load.">
          Try refreshing the page.
        </QAEmpty>
      ) : entries.length === 0 ? (
        <QAEmpty
          inline
          eyebrow="Nothing yet"
          title={kind ? 'No activity of this type.' : 'No recorded activity across the book.'}
        >
          {kind ? 'Clear the filter to see everything.' : 'Activity appears here as the team records notes, advice and compliance updates.'}
        </QAEmpty>
      ) : (
        <ol className="relative border-l border-hairline ml-3" data-testid="activity-feed">
          {entries.map((e) => {
            const meta = KIND_META[e.kind];
            const Icon = meta.icon;
            const isLong = !!e.detail && e.detail.length > LONG_DETAIL;
            const isOpen = expanded.has(e.id);
            const detail = e.detail && isLong && !isOpen ? `${e.detail.slice(0, LONG_DETAIL)}…` : e.detail;
            return (
              <li key={e.id} className="relative pl-8 pb-6 last:pb-0">
                <span
                  className={`absolute -left-[9px] top-1 grid place-items-center w-[18px] h-[18px] rounded-full bg-paper border border-hairline ${meta.tone}`}
                  aria-hidden="true"
                >
                  <Icon size={11} strokeWidth={2.2} />
                </span>
                <div className="qa-card p-4">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {e.client && (
                        <Link
                          to={`/clients/${e.client.id}`}
                          className="text-sm font-semibold text-rust hover:text-rust-deep underline-offset-2 hover:underline"
                        >
                          {e.client.name}
                        </Link>
                      )}
                      <span className="text-sm text-ink-soft">{e.title}</span>
                    </div>
                    <span className="qa-mono text-[11px] text-ink-mute whitespace-nowrap">
                      {formatDateAU(e.at)} · {formatRelativeDays(e.at)}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className={`qa-badge qa-badge--mute ${meta.tone}`}>{meta.label}</span>
                    {(e.badges ?? []).map((b, i) => (
                      <span key={i} className="qa-badge qa-badge--mute">{b}</span>
                    ))}
                  </div>

                  {detail && (
                    <p className="text-sm text-ink-soft whitespace-pre-wrap mt-3">{detail}</p>
                  )}

                  {isLong && (
                    <button
                      type="button"
                      className="text-[11px] uppercase tracking-widest font-bold text-ink-mute hover:text-ink mt-3"
                      onClick={() => toggle(e.id)}
                    >
                      {isOpen ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
